import { memo, useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import styled from "styled-components";

import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { useLoginStore } from "stores/LoginStore";
import { useUserStore } from "stores/UserStore";

import { notifyError, notifySuccess } from "utility/notify";
import throwIf from "utility/throw_if";
import { FOTS } from "utility/typescript";

import useAnalytics from "effect/use_analytics";

import LogoIcon from "layouts/components/Icon/Logo";

// -----------------------------------------------------------------------------

const RegisterPage = memo(function RegisterPage(): JSX.Element {
  useAnalytics("Register User");

  //
  const [loginStore, loginActions] = useLoginStore();
  const [userStore] = useUserStore();
  const navigate = useNavigate();

  //
  const getParam = buildParams();
  const [fullName, setFullName] = useState(getParam("name"));
  const [emailAddress, setEmailAddress] = useState(getParam("email_address"));
  const [organizationName, setOrganizationName] = useState(
    getParam("organization")
  );

  //
  const autoLogin = useCallback(
    async (emailAddress: string) => {
      try {
        const response = (await loginActions.login(emailAddress)) as any;
        throwIf(
          response?.error,
          response?.message || "There was an error logging in"
        );

        //
        if (response.redirectURL) {
          // nosemgrep: javascript.browser.security.open-redirect.js-open-redirect
          window.location.href = response.redirectURL;
        }
      } catch (error) {
        notifyError(
          `Could not automatically login: ${
            (error as Error).message
          }. Please login manually.`
        );
      }
    },
    [loginActions]
  );

  //
  useEffect(() => {
    if (loginStore.registerError) {
      return notifyError(`Could not register: ${loginStore.registerError}`);
    }

    if (loginStore.registeredEmail) {
      autoLogin(loginStore.registeredEmail);
      return notifySuccess("Your ThreatKey account has been created!");
    }
  }, [loginStore.registeredEmail, loginStore.registerError, autoLogin]);

  //
  function submitForm() {
    if (loginStore.registering) {
      return;
    }

    try {
      const emailAddressFinal = emailAddress.trim();
      throwIfInvalid(emailAddressFinal, "Missing email address");

      const fullNameFinal = fullName.trim();
      throwIfInvalid(fullNameFinal, "Missing name");

      const organizationNameFinal = organizationName.trim();
      throwIfInvalid(organizationNameFinal, "Missing organization name");

      //
      loginActions.register(
        emailAddressFinal,
        fullNameFinal,
        organizationNameFinal
      );
    } catch (error) {
      notifyError((error as Error).message);
    }
  }

  //

  if (userStore.user.email) {
    return <Navigate to="/analytics" />;
  }

  if (getParam("autosubmit") !== "" && !loginStore.registering) {
    // remove autosubmit from URL
    const url = new URL(window.location.href);
    url.searchParams.delete("autosubmit");

    // change URL without refresh or trigger
    window.history.replaceState(null, "", url.toString());

    //
    submitForm();
  }

  if (getParam("autosubmit") !== "" && !loginStore.registering) {
    // remove autosubmit from URL
    const url = new URL(window.location.href);
    url.searchParams.delete("autosubmit");

    // change URL without refresh or trigger
    window.history.replaceState(null, "", url.toString());

    //
    submitForm();
  }

  //
  return loginStore.registeredEmail ? (
    <Navigate
      to="/check-email"
      state={{ justRegisteredEmail: loginStore.registeredEmail }}
    />
  ) : (
    <Paper
      elevation={1}
      sx={{
        display: "flex",
        flexDirection: "column",
        margin: "auto",
        maxWidth: "420px",
        padding: 2,
        position: "relative",
        width: "100%",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          bottom: "calc(100% + 32px)",
          left: 0,
          right: 0,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          height: 0,
        }}
      >
        <Box
          component={LogoIcon}
          sx={{ fill: "#FFF", width: "164.17px", height: "40px" }}
        />
      </Box>

      {loginStore.registerError ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          {loginStore.registerError || "There was an error"}
        </Alert>
      ) : null}

      <Typography variant="h5" fontWeight="bold">
        Get Started with ThreatKey for Free
      </Typography>

      <Typography variant="body1">
        Fill in the below form to create your ThreatKey account.
      </Typography>

      <Box sx={{ mt: 2.5 }}>
        <TextFieldStyled
          fullWidth
          label="Full Name"
          variant="outlined"
          value={fullName}
          onChange={(e: FOTS) => setFullName(e.target.value)}
          disabled={loginStore.registering}
        />

        <TextFieldStyled
          fullWidth
          label="Email Address"
          variant="outlined"
          value={emailAddress}
          onChange={(e: FOTS) => setEmailAddress(e.target.value)}
          disabled={loginStore.registering}
        />

        <TextFieldStyled
          fullWidth
          label="Organization Name"
          variant="outlined"
          value={organizationName}
          onChange={(e: FOTS) => setOrganizationName(e.target.value)}
          disabled={loginStore.registering}
        />
      </Box>

      <FormGroup sx={{ mt: 1 }}>
        <FormControlLabel
          control={<Checkbox checked={true} />}
          label={
            <Typography variant="body1">
              I accept the{" "}
              <a
                href="https://www.threatkey.com/policies/terms-of-service"
                target="_blank"
                rel="noopener noreferrer"
              >
                terms &amp; conditions
              </a>
              .
            </Typography>
          }
        />
      </FormGroup>

      <Box sx={{ mt: 1 }}>
        <LoadingButton
          fullWidth
          variant="contained"
          onClick={submitForm}
          disabled={loginStore.registering}
          loading={loginStore.registering}
        >
          Register
        </LoadingButton>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography variant="body1">
          Already have an account?{" "}
          <Button
            size="small"
            onClick={() => {
              loginActions.resetErrors();
              navigate("/login");
            }}
            sx={{ fontWeight: "bold" }}
          >
            LOGIN
          </Button>
        </Typography>
      </Box>
    </Paper>
  );
});

export default RegisterPage;

// -----------------------------------------------------------------------------

function buildParams() {
  const params = new URLSearchParams(window.location.search);

  //
  return (key: string, defaultValue: string = "") =>
    params.get(key) ?? defaultValue;
}

function throwIfInvalid(value: string, message: string) {
  throwIf(~~value?.length < 1, message);
}

// -----------------------------------------------------------------------------

const TextFieldStyled = styled(TextField)`
  & + & {
    margin-top: 16px;
  }
`;
